import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search as SearchIcon } from "lucide-react";
import Navbar from "../components/Navbar";
import PosterCard from "../components/PosterCard";
import WheelBrowser, { cardRow } from "../components/clickwheel/WheelBrowser";
import { useTheme } from "../context/ThemeContext";
import api from "../lib/api";
import { trackEvent } from "../lib/track";
import { GENRES, genreName } from "../lib/genres";

export default function Search() {
  const wheel = useTheme().skin === "clickwheel";
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const genre = params.get("genre") || "";
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2 && !genre) { setResults([]); setLoading(false); return; }
    const controller = new AbortController();
    setLoading(true);
    if (q.trim()) trackEvent("search", { term: q.trim(), genre });
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (genre) qs.set("genre", genre);
    api.get(`/catalog/search?${qs.toString()}`, { signal: controller.signal })
      .then(({ data }) => { if (!controller.signal.aborted) setResults(data.results); })
      .catch(() => { if (!controller.signal.aborted) setResults([]); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort(); // cancel in-flight request if the query changes
  }, [q, genre]);

  const setGenre = (g) => {
    const next = new URLSearchParams(params);
    if (g) next.set("genre", g); else next.delete("genre");
    setParams(next, { replace: true });
  };

  const heading = q
    ? <>Results for <span className="text-[var(--accent)]">"{q}"</span>{genre ? <> in <span className="text-[var(--accent)]">{genreName(genre)}</span></> : null}</>
    : genre ? <>Browsing <span className="text-[var(--accent)]">{genreName(genre)}</span></> : "Search";

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Navbar />
      <main className="px-4 lg:px-14 pt-6 pb-20" data-testid="search-page">
        <h1 className="font-head text-3xl sm:text-4xl tracking-wide text-[var(--text-primary)]">{heading}</h1>

        {/* Phone width: the header search field is collapsed to an icon, so the page carries its own. */}
        <form role="search" className="mt-4 flex gap-2 sm:hidden" data-testid="search-page-form"
          onSubmit={(e) => { e.preventDefault(); const v = e.currentTarget.elements.q.value.trim(); const next = new URLSearchParams(params); if (v) next.set("q", v); else next.delete("q"); setParams(next); }}>
          <input key={q} name="q" type="search" defaultValue={q} placeholder="Search titles…" aria-label="Search titles" data-testid="search-page-input"
            className="min-w-0 flex-1 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] px-3 py-2.5 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]" />
          <button type="submit" data-testid="search-page-submit" className="inline-flex flex-none items-center gap-1.5 rounded-[var(--card-radius)] bg-[var(--accent)] px-4 text-sm font-bold text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)]">
            <SearchIcon size={15} /> Search
          </button>
        </form>

        {/* Genre filter */}
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="search-genre-filter">
          <span className="text-xs font-semibold uppercase tracking-widest text-[var(--text-muted)]">Genre</span>
          <button onClick={() => setGenre("")} data-testid="search-genre-any" className={`genre-pill ${!genre ? "genre-pill-active" : ""}`}>Any</button>
          <select
            value={genre}
            onChange={(e) => setGenre(e.target.value)}
            data-testid="search-genre-select"
            className="rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] px-3 py-2 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]"
          >
            <option value="">All genres</option>
            {GENRES.map((g) => <option key={g.slug} value={g.slug}>{g.name}</option>)}
          </select>
        </div>

        {loading && <p className="mt-6 text-[var(--text-secondary)]">Searching…</p>}

        {!loading && (q || genre) && results.length === 0 && (
          <div className="mt-16 flex flex-col items-center text-center text-[var(--text-secondary)]" data-testid="search-empty">
            <SearchIcon size={40} className="mb-3 opacity-50" />
            <p>No titles matched{q ? ` "${q}"` : ""}{genre ? ` in ${genreName(genre)}` : ""}.</p>
          </div>
        )}

        {!loading && !q && !genre && (
          <p className="mt-6 text-[var(--text-secondary)]">Type in the search bar or pick a genre to find movies and series.</p>
        )}

        {results.length > 0 && wheel && <WheelBrowser rows={results.map((it) => cardRow(it))} title={`Results for “${q || ""}”`} testid="wheel-search" />}
        {results.length > 0 && !wheel && (
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6" data-testid="search-results-grid">
            {results.map((it) => (
              <div key={it.id} className="w-full [&>div]:w-full">
                <PosterCard item={it} />
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
